// Datenschutzerklärung — Deutsch. Übersetzung aus Gefälligkeit; maßgeblich ist die italienische Fassung.
// Abschnitts-`id`s und Reihenfolge müssen mit privacy.it.js übereinstimmen (geprüft von privacy.test.js).
import { CONTROLLER, controllerAddressLine, hasPostalAddress } from "@/lib/legal";

// Il paese nella lingua di chi legge: CONTROLLER.country resta il valore postale.
const COUNTRY = "Italien";

const identity = [
  ["Verantwortlicher", CONTROLLER.legalName],
  CONTROLLER.tradeName && ["Geschäftsbezeichnung", CONTROLLER.tradeName],
  ["Rechtsform", "Einzelunternehmen (impresa individuale) nach italienischem Recht"],
  ["Niederlassungsland", COUNTRY],
  CONTROLLER.vat && ["USt-IdNr.", CONTROLLER.vat],
  CONTROLLER.taxCode && ["Italienische Steuernummer", CONTROLLER.taxCode],
  CONTROLLER.reaNumber && ["REA-Eintragung", CONTROLLER.reaNumber],
  hasPostalAddress() && ["Sitz", controllerAddressLine()],
  ["E-Mail", CONTROLLER.email],
  CONTROLLER.pec && ["Zertifizierte E-Mail (PEC)", CONTROLLER.pec],
  CONTROLLER.phone && ["Telefon", CONTROLLER.phone],
].filter(Boolean);

const doc = {
  title: "Datenschutzerklärung",
  subtitle:
    "Informationen zur Verarbeitung personenbezogener Daten gemäß Art. 13 und 14 der Verordnung (EU) 2016/679 (DSGVO) und dem italienischen Gesetzesdekret 196/2003 in der Fassung des Gesetzesdekrets 101/2018.",
  updatedLabel: "Zuletzt aktualisiert",
  versionLabel: "Version",
  tocLabel: "Inhalt",
  authoritativeNote:
    "Dies ist eine Übersetzung aus Gefälligkeit. Der Verantwortliche ist in Italien niedergelassen: Bei Abweichungen ist die italienische Fassung dieser Erklärung maßgeblich.",
  backLabel: "Zurück zur Website",

  sections: [
    {
      id: "titolare",
      h: "1. Wer Ihre Daten verarbeitet",
      blocks: [
        {
          p: `Verantwortlicher ist ${CONTROLLER.legalName}, Einzelunternehmen ${
            hasPostalAddress() ? `mit Sitz in ${controllerAddressLine()}` : `mit Niederlassung in ${COUNTRY}`
          }, Betreiber des Dienstes VisioScreening (nachfolgend „der Dienst“, erreichbar unter visioscreening.com).`,
        },
        {
          p: `Der Verantwortliche ist unter ${CONTROLLER.email} für jede Mitteilung zu dieser Erklärung erreichbar, einschließlich der Ausübung der in Abschnitt 10 beschriebenen Rechte. Wer die vollständigen Identifikationsdaten des Verantwortlichen benötigt — einschließlich USt-IdNr. und Sitz — kann sie unter derselben Adresse anfordern und erhält sie kostenfrei.`,
        },
        { table: { head: ["", ""], rows: identity } },
        {
          p: "Ein Datenschutzbeauftragter wurde nicht bestellt: Die Verarbeitung fällt nicht unter die in Art. 37 DSGVO genannten Fälle, da sie weder eine umfangreiche regelmäßige und systematische Überwachung noch eine umfangreiche Verarbeitung besonderer Kategorien personenbezogener Daten umfasst. Bei allen datenschutzrechtlichen Fragen können Sie sich direkt an die oben genannten Kontaktdaten wenden.",
        },
      ],
    },

    {
      id: "ruoli",
      h: "2. Zwei Arten von Daten, zwei unterschiedliche Rollen",
      blocks: [
        {
          p: "VisioScreening ist ein Werkzeug für Fachkräfte: Wer es einsetzt, ist Arzt, Optiker, Fachkraft für Arbeitssicherheit oder eine andere medizinische Fachperson, die Sehtests bei einer Bildschirmarbeitskraft durchführt. Dadurch entstehen zwei vollständig getrennte Datenkategorien, die keinesfalls vermischt werden dürfen.",
        },
        {
          table: {
            head: ["Daten", "Wer ist verantwortlich", "Wo sie liegen"],
            rows: [
              [
                "Daten der Fachperson, die den Dienst nutzt (Konto, Einrichtung, Zahlung)",
                `${CONTROLLER.legalName} ist Verantwortlicher`,
                "Auf den Servern des Dienstes",
              ],
              [
                "Daten der getesteten Arbeitskraft (Identität, Antworten, Ergebnisse, Befundbericht)",
                "Die Fachperson bzw. die Gesundheitseinrichtung ist eigenständiger Verantwortlicher",
                "Ausschließlich auf dem Gerät der Fachperson",
              ],
            ],
          },
        },
        {
          note: "Klinische Daten der Arbeitskraft werden niemals an unsere Server übermittelt. Sie verbleiben für die Dauer des Screenings im Arbeitsspeicher des Browsers, und der PDF-Bericht wird lokal auf dem Gerät der Fachperson erzeugt. Für diese Daten sind wir weder Verantwortlicher noch Auftragsverarbeiter, weil sie uns nie erreichen. Abschnitt 4 erläutert dies im Einzelnen.",
        },
      ],
    },

    {
      id: "dati",
      h: "3. Welche Daten wir verarbeiten und wozu",
      blocks: [
        {
          p: "Wir verarbeiten ausschließlich das, was nötig ist, um Ihnen ein Konto bereitzustellen, Berichte erzeugen zu lassen und die Zahlung einzuziehen. Wir betreiben kein Profiling, versenden keine unaufgeforderten Newsletter, geben keine Daten zu Marketingzwecken an Dritte weiter und setzen keine Werkzeuge zur Verhaltensanalyse ein.",
        },
        {
          table: {
            head: ["Datenkategorie", "Zweck", "Rechtsgrundlage", "Speicherdauer"],
            rows: [
              [
                "Kontodaten: E-Mail-Adresse, Name (freiwillig), Passwort ausschließlich als bcrypt-Hash; bei Anmeldung mit Google zusätzlich Name, E-Mail und Profilbild, die Google übermittelt",
                "Konto anlegen und verwalten, Sie authentifizieren, Passwortwiederherstellung ermöglichen",
                "Art. 6 Abs. 1 lit. b DSGVO — Vertragserfüllung",
                "Für die Dauer des Kontos und bis zu 30 Tage nach einem Löschverlangen (Backups bis zu 90 Tage)",
              ],
              [
                "Daten der Einrichtung: Bezeichnung und Anschrift der Einrichtung, Name des Arztes, hochgeladenes Logo und Unterschriftenbild",
                "Kopfzeile des PDF-Berichts mit den Daten Ihrer Praxis versehen",
                "Art. 6 Abs. 1 lit. b DSGVO — Vertragserfüllung",
                "Bis zur Löschung des Kontos oder zur Änderung der Konfiguration",
              ],
              [
                "Berichtsregister: zufällige Berichtskennung, Zeitstempel, Sprache, Angabe ob kostenlos oder bezahlt, sowie ein gesalzener SHA-256-Hash der IP-Adresse",
                "Die erworbene Lizenz durchsetzen, bereits genutzte Gratisberichte zählen, Missbrauch des Gratisberichts (serielles Anlegen von Konten) erkennen",
                "Art. 6 Abs. 1 lit. b und lit. f DSGVO — Vertragserfüllung und berechtigtes Interesse an der Missbrauchsprävention",
                "Die Zeile bleibt für die Dauer des Kontos, weil sie der Nachweis der erworbenen Lizenz ist; der IP-Hash wird entfernt, sobald das wenige Tage umfassende Zeitfenster zur Missbrauchserkennung abgelaufen ist",
              ],
              [
                "Zahlungsdaten: Kennung der Zahlungssitzung, Betrag, Währung, Status sowie die bei Stripe eingegebenen Rechnungs- und Steuerdaten",
                "Einmalentgelt einziehen, Buchungsbelege erstellen und aufbewahren",
                "Art. 6 Abs. 1 lit. b DSGVO — Vertragserfüllung; Art. 6 Abs. 1 lit. c DSGVO — steuer- und handelsrechtliche Pflichten",
                "Buchungsbelege 10 Jahre ab der Buchung (Art. 2220 ital. Zivilgesetzbuch und Steuerrecht); die technischen Transaktionsdaten bis zur Löschung des Kontos",
              ],
              [
                "Technische und Sicherheitsdaten: IP-Adresse, Browsertyp, Zeitstempel der Anfragen, fehlgeschlagene Anmeldeversuche, Sitzungs- und Passwort-Zurücksetzungs-Token",
                "Den Betrieb aufrechterhalten, vor unbefugtem Zugriff schützen, Passwort-Rateversuche begrenzen",
                "Art. 6 Abs. 1 lit. f DSGVO — berechtigtes Interesse an Netz- und Informationssicherheit",
                "Anmeldeversuche: Minuten. Token: bis zum Ablauf. Protokolle der Hosting-Anbieter: in der Regel 30 Tage",
              ],
              [
                "Korrespondenz: Inhalt der E-Mails oder Nachrichten, die Sie uns zur Unterstützung senden",
                "Ihre Anfragen beantworten und etwaige Beanstandungen bearbeiten",
                "Art. 6 Abs. 1 lit. b und lit. f DSGVO — Vertragserfüllung und berechtigtes Interesse an der Rechtsverteidigung",
                "Bis zu 24 Monate nach dem letzten Kontakt, sofern kein Rechtsstreit anhängig ist",
              ],
            ],
          },
        },
        {
          p: "Die Angabe der in den Formularen als Pflicht gekennzeichneten Daten ist erforderlich, um das Konto zu aktivieren und den Dienst zu nutzen: Ohne sie ist die Bereitstellung technisch nicht möglich. Die Angabe weiterer Daten ist freiwillig; ihr Fehlen beeinträchtigt die Nutzung des Dienstes nicht.",
        },
      ],
    },

    {
      id: "pazienti",
      h: "4. Die Daten der getesteten Arbeitskraft",
      blocks: [
        {
          p: "Der Anamnesebogen, die Identifikationsdaten der Arbeitskraft, die Antworten auf die sieben Tests und der abschließende Bericht sind Gesundheitsdaten, also besondere Kategorien personenbezogener Daten im Sinne des Art. 9 DSGVO. Der Dienst ist so gebaut, dass wir sie niemals erhalten.",
        },
        {
          ul: [
            "Während des Screenings eingegebene Daten verbleiben im Arbeitsspeicher des Browsers, auf dem Gerät der Fachperson, und werden zu keinem Zeitpunkt an unsere Server übermittelt.",
            "Der PDF-Bericht wird vollständig im Browser erzeugt: Er läuft nicht über uns, wir speichern ihn nicht und bewahren keine Kopie auf.",
            "Wenn Sie das Screening zur Zahlung unterbrechen, werden die eingegebenen Daten für höchstens zwei Stunden vorübergehend im sessionStorage des Browsers gespeichert, damit Sie dort weitermachen können, wo Sie aufgehört haben. Der sessionStorage wird beim Schließen des Browsers geleert; Logo und Unterschrift werden nie dorthin geschrieben.",
            "Das Einzige, was beim Herunterladen eines Berichts unsere Server erreicht, ist eine zufällige Berichtskennung ohne jeden Bezug zur Arbeitskraft: Sie dient allein dazu, den Zähler der verfügbaren Berichte zu verringern.",
          ],
        },
        {
          note: "Da wir die Gesundheitsdaten der Arbeitskraft nie erhalten, gehen wir davon aus, dass die Voraussetzungen für eine Bestellung als Auftragsverarbeiter nach Art. 28 DSGVO nicht vorliegen. Gleichwohl schließen wir auf Wunsch einen Auftragsverarbeitungsvertrag ab oder stellen eine schriftliche Erklärung aus, die die hier dargestellte Architektur beschreibt: Schreiben Sie an die in Abschnitt 14 genannte Adresse.",
        },
        {
          p: `Sämtliche Pflichten hinsichtlich der Daten der Arbeitskraft verbleiben bei der Fachperson, die den Dienst nutzt: die Rechtsgrundlage der Verarbeitung bestimmen (in der Regel Art. 9 Abs. 2 lit. h DSGVO für die Arbeitsmedizin, in Verbindung mit Art. 41 des ital. Gesetzesdekrets 81/2008), die eigene Datenschutzinformation erteilen, den Bericht sicher aufbewahren und die Schweigepflicht wahren. ${CONTROLLER.legalName} haftet nicht für die Verwendung des Berichts durch die Fachperson nach dessen Erstellung.`,
        },
      ],
    },

    {
      id: "destinatari",
      h: "5. An wen wir Daten weitergeben",
      blocks: [
        {
          p: "Wir verkaufen und vermieten Ihre Daten nicht. Wir geben sie ausschließlich an die Dienstleister weiter, die den Dienst technisch möglich machen; sie sind als Auftragsverarbeiter nach Art. 28 DSGVO bestellt und dürfen die Daten nur nach unseren Weisungen verarbeiten.",
        },
        {
          table: {
            head: ["Dienstleister", "Rolle", "Betroffene Daten"],
            rows: [
              ["Stripe Payments Europe, Ltd. (Irland)", "Abwicklung von Kartenzahlungen", "Rechnungsdaten, Betrag, Ergebnis. Kartendaten verarbeitet Stripe unmittelbar; sie werden uns nie mitgeteilt"],
              ["MongoDB, Inc. / MongoDB Atlas", "Verwaltete Datenbank", "Sämtliche Konto- und Lizenzdaten"],
              ["Render Services, Inc.", "Hosting der serverseitigen Anwendung", "An die API übermittelte Daten und technische Protokolle"],
              ["Netlify, Inc.", "Hosting und CDN für Website und Web-App", "Verbindungsdaten (IP-Adresse, User-Agent)"],
              ["Anbieter für Transaktions-E-Mails (Resend / konfigurierter SMTP-Anbieter)", "Versand von System-E-Mails, etwa zum Zurücksetzen des Passworts", "E-Mail-Adresse und Nachrichteninhalt"],
              ["Google Ireland Limited", "Anmeldung mit Google, nur wenn Sie sie nutzen", "Google-Kennung, Name, E-Mail und Profilbild"],
              ["Steuer- und Buchhaltungsberatung", "Verwaltungs- und steuerrechtliche Pflichten", "Rechnungsdaten"],
            ],
          },
        },
        {
          p: "Daten können ferner an Behörden übermittelt werden, soweit dies gesetzlich vorgeschrieben oder zur Geltendmachung, Ausübung oder Verteidigung von Rechtsansprüchen erforderlich ist. Eine aktuelle Liste der Auftragsverarbeiter erhalten Sie auf Anfrage unter " + CONTROLLER.email + ".",
        },
      ],
    },

    {
      id: "trasferimenti",
      h: "6. Übermittlungen außerhalb des Europäischen Wirtschaftsraums",
      blocks: [
        {
          p: "Einige der oben genannten Dienstleister haben ihren Sitz in den Vereinigten Staaten oder verarbeiten Daten möglicherweise außerhalb des Europäischen Wirtschaftsraums. In diesem Fall stützt sich die Übermittlung auf eine der Garantien des Kapitels V DSGVO: einen Angemessenheitsbeschluss der Europäischen Kommission — insbesondere das EU-U.S. Data Privacy Framework für zertifizierte Anbieter — oder die von der Europäischen Kommission mit Durchführungsbeschluss (EU) 2021/914 erlassenen Standardvertragsklauseln, erforderlichenfalls ergänzt um zusätzliche Maßnahmen.",
        },
        {
          p: "Einige der eingesetzten Dienste werden in Regionen außerhalb der Europäischen Union betrieben, insbesondere in den Vereinigten Staaten. Sie können jederzeit und kostenfrei eine aktuelle Angabe darüber, wo die Daten liegen, sowie eine Kopie der für den jeweiligen Anbieter getroffenen Garantien unter " + CONTROLLER.email + " anfordern.",
        },
      ],
    },

    {
      id: "conservazione",
      h: "7. Wie lange wir Daten speichern",
      blocks: [
        { p: "Die Speicherfristen sind je Kategorie in der Tabelle in Abschnitt 3 angegeben. Zusammengefasst:" },
        {
          ul: [
            "Kontodaten bleiben gespeichert, solange das Konto besteht. Die Anwendung bietet keine Selbstlöschung: Die Löschung nimmt der Verantwortliche manuell vor, auf eine an die in Abschnitt 14 genannte Adresse gerichtete Anfrage, unverzüglich und in jedem Fall binnen 30 Tagen; sie umfasst Konto, Lizenz, Einrichtungskonfiguration, Sitzungen und Berichtsaufzeichnungen.",
            "Der Hash der IP-Adresse dient dazu, Missbrauch des Gratisberichts über ein Zeitfenster von wenigen Tagen zu erkennen. Danach hat er keinen Nutzen mehr und wird im Rahmen der regelmäßigen Kontrollen des Verantwortlichen entfernt; die zugehörige Berichtszeile bleibt — ohne den Hash — bestehen, weil sie der Nachweis der Lizenz ist.",
            "Buchhaltungs- und Steuerunterlagen zur Zahlung werden 10 Jahre aufbewahrt, wie es italienisches Recht vorschreibt: Diese Pflicht geht einem Löschverlangen vor und betrifft nur die Buchungsbelege, nicht die gesamte technische Historie der Zahlung.",
            "Technische und Sicherheitsdaten sind kurzlebig: Zähler für Anmeldeversuche halten Minuten, Sitzungs- und Zurücksetzungs-Token laufen von selbst ab, und die Protokolle der Hosting-Anbieter folgen deren eigener Rotation.",
            "Vom Datenbankanbieter verwaltete Backups folgen der Rotation des genutzten Tarifs: Die Löschung aus den aktiven Systemen erfolgt sofort, die aus den Backups mit dem nächsten Rotationszyklus.",
          ],
        },
        {
          note: "Dass die Löschung manuell erfolgt, schmälert weder ihren Umfang noch ihre Fristen: Art. 17 DSGVO wird durch eine Nachricht an den Verantwortlichen ausgeübt, und die Anfrage wird innerhalb der Fristen aus Abschnitt 10 bearbeitet. Wenn Sie die Löschung überprüfen möchten, bitten Sie in derselben E-Mail darum und erhalten eine schriftliche Bestätigung.",
        },
      ],
    },

    {
      id: "cookie",
      h: "8. Cookies und lokale Speicherung",
      blocks: [
        {
          p: "VisioScreening verwendet keine Profiling-Cookies, keine Werbe-Cookies und keine Statistik- oder Verhaltensanalyse-Werkzeuge. Deshalb gibt es kein Einwilligungsbanner: Die eingesetzten Technologien sind ausschließlich technisch notwendig, und die Leitlinien der italienischen Datenschutzbehörde vom 10. Juni 2021 verlangen dafür keine Einwilligung.",
        },
        {
          table: {
            head: ["Name", "Art", "Funktion", "Dauer"],
            rows: [
              ["session_token", "Technisches Cookie (httpOnly, Secure)", "Hält die Sitzung nach der Anmeldung aufrecht", "Bis zum Ablauf der Sitzung oder zur Abmeldung"],
              ["vs_token", "localStorage", "Kopie des Sitzungstokens; erforderlich, weil Website und API auf verschiedenen Domains liegen und manche Browser Drittanbieter-Cookies blockieren", "Bis zur Abmeldung oder zum Löschen der Browserdaten"],
              ["vs_lang", "localStorage", "Merkt sich die gewählte Sprache der Oberfläche", "Bis zum Löschen der Browserdaten"],
              ["visioscreen_defaults_v1", "localStorage", "Merkt sich die Standardangaben zu Einrichtung und Arzt, damit sie nicht bei jedem Screening neu eingegeben werden müssen", "Bis zum Löschen der Browserdaten"],
              ["vs_pending_report", "sessionStorage", "Bewahrt ein zur Zahlung unterbrochenes Screening auf, damit es fortgesetzt werden kann", "Höchstens 2 Stunden und in jedem Fall beim Schließen des Browsers gelöscht"],
            ],
          },
        },
        {
          p: "Die Zahlung erfolgt auf einer von Stripe gehosteten Seite, die nach eigener Datenschutzerklärung eigene technische und Betrugspräventions-Cookies setzt. Die öffentlichen Seiten laden keine Ressourcen von Drittanbieter-Domains: Bilder, Schriften und Skripte werden von unserer eigenen Domain ausgeliefert, sodass der bloße Besuch Ihre IP-Adresse an keinen externen Anbieter übermittelt. Sie können Cookies und lokale Speicher jederzeit in den Browsereinstellungen löschen; das Deaktivieren technisch notwendiger Cookies macht die Anmeldung an der Anwendung jedoch unmöglich.",
        },
      ],
    },

    {
      id: "sicurezza",
      h: "9. Sicherheit",
      blocks: [
        { p: "Wir treffen dem Risiko angemessene technische und organisatorische Maßnahmen gemäß Art. 32 DSGVO, darunter:" },
        {
          ul: [
            "Datenübertragung ausschließlich über verschlüsselte Verbindungen (HTTPS/TLS);",
            "Passwörter ausschließlich als bcrypt-Hash gespeichert, nie im Klartext und nicht umkehrbar;",
            "Sitzungstoken in httpOnly- und Secure-Cookies mit Ablauf;",
            "Datentrennung je Konto: Kein Nutzer kann auf Konfiguration oder Berichte eines anderen zugreifen;",
            "automatische Begrenzung wiederholter Anmeldeversuche;",
            "IP-Adressen nur als SHA-256-Hash mit geheimem Salt gespeichert, ohne dieses Salt nicht auf die Ursprungsadresse zurückführbar;",
            "Datenminimierung an der Quelle: Gesundheitsdaten verlassen das Gerät der Fachperson nie.",
          ],
        },
        {
          p: "Keine Sicherheitsmaßnahme ist absolut. Im Fall einer Verletzung des Schutzes personenbezogener Daten mit Risiko für die Rechte und Freiheiten der betroffenen Personen melden wir dies der italienischen Datenschutzbehörde binnen 72 Stunden und benachrichtigen bei hohem Risiko die betroffenen Personen, gemäß Art. 33 und 34 DSGVO.",
        },
      ],
    },

    {
      id: "diritti",
      h: "10. Ihre Rechte",
      blocks: [
        { p: "Sie können jederzeit die in den Art. 15 bis 22 DSGVO eingeräumten Rechte ausüben:" },
        {
          ul: [
            "Auskunft: Bestätigung verlangen, ob eine Verarbeitung stattfindet, und eine Kopie erhalten (Art. 15);",
            "Berichtigung: unrichtige Daten korrigieren oder unvollständige Daten vervollständigen (Art. 16);",
            "Löschung: Entfernung der Daten verlangen, die wir nicht gesetzlich aufbewahren müssen (Art. 17);",
            "Einschränkung: in den vorgesehenen Fällen die Aussetzung der Verarbeitung verlangen (Art. 18);",
            "Datenübertragbarkeit: die von Ihnen bereitgestellten Daten in einem strukturierten, maschinenlesbaren Format erhalten oder an einen anderen Verantwortlichen übermitteln lassen (Art. 20);",
            "Widerspruch: aus Gründen, die sich aus Ihrer besonderen Situation ergeben, jederzeit Verarbeitungen widersprechen, die auf berechtigtem Interesse beruhen (Art. 21).",
          ],
        },
        {
          p: `Zur Ausübung dieser Rechte schreiben Sie an ${CONTROLLER.email}${CONTROLLER.pec ? ` oder per zertifizierter E-Mail an ${CONTROLLER.pec}` : ""}. Wir antworten binnen eines Monats nach Eingang der Anfrage; bei besonders komplexen Anfragen kann sich die Frist um zwei Monate verlängern, worüber wir Sie informieren. Die Ausübung der Rechte ist unentgeltlich, es sei denn, Anträge sind offenkundig unbegründet oder exzessiv.`,
        },
        {
          p: "Sind Sie der Ansicht, dass die Verarbeitung Ihrer Daten gegen die DSGVO verstößt, haben Sie das Recht, Beschwerde bei der italienischen Datenschutzbehörde einzulegen — Garante per la protezione dei dati personali, Piazza Venezia 11, 00187 Rom, Italien; Telefon +39 06 696771; E-Mail garante@gpdp.it; zertifizierte E-Mail protocollo@pec.gpdp.it; www.garanteprivacy.it — oder bei der Aufsichtsbehörde des Mitgliedstaats Ihres Aufenthalts. Das Recht auf einen gerichtlichen Rechtsbehelf bleibt unberührt.",
        },
      ],
    },

    {
      id: "automatizzate",
      h: "11. Automatisierte Entscheidungen und Profiling",
      blocks: [
        {
          p: "Wir führen keine automatisierten Entscheidungen und kein Profiling im Sinne des Art. 22 DSGVO durch. Die Testergebnisse werden lokal auf dem Gerät berechnet, dienen ausschließlich der Unterstützung des Screenings und ersetzen in keiner Weise die Beurteilung und Diagnose der medizinischen Fachperson.",
        },
      ],
    },

    {
      id: "minori",
      h: "12. Minderjährige",
      blocks: [
        {
          p: "Der Dienst richtet sich an Fachkräfte in Ausübung ihrer Tätigkeit und ist nicht für Minderjährige bestimmt. Wir erheben bei der Kontoregistrierung wissentlich keine Daten von Minderjährigen. Wenn Sie meinen, dass eine minderjährige Person ein Konto angelegt hat, teilen Sie es uns mit — wir löschen es.",
        },
      ],
    },

    {
      id: "modifiche",
      h: "13. Änderungen dieser Erklärung",
      blocks: [
        {
          p: "Diese Erklärung kann aktualisiert werden, um Änderungen des Dienstes, der eingesetzten Dienstleister oder der anwendbaren Rechtslage abzubilden. Die geltende Fassung wird stets auf dieser Seite veröffentlicht, mit Datum der letzten Aktualisierung und Versionsnummer. Bei wesentlichen Änderungen informieren wir registrierte Nutzer vor deren Wirksamwerden per E-Mail oder innerhalb der Anwendung.",
        },
      ],
    },

    {
      id: "contatti",
      h: "14. Kontakt",
      blocks: [
        {
          p: `Bei Fragen zu dieser Erklärung oder zur Verarbeitung Ihrer personenbezogenen Daten schreiben Sie an ${CONTROLLER.email}${CONTROLLER.pec ? ` oder ${CONTROLLER.pec}` : ""}. Es ist dieselbe Adresse wie die des Supports — bewusst so gewählt und täglich betreut: Anliegen zum Datenschutz werden dort als solche erkannt und behandelt. Wenn Sie eine Postanschrift des Verantwortlichen benötigen — für ein Einschreiben oder eine förmliche Beschwerde — fordern Sie sie dort an; sie wird Ihnen kostenfrei mitgeteilt.`,
        },
      ],
    },
  ],
};

export default doc;
